import { useCallback, useEffect, useMemo, useState } from 'react';
import type { MicroReport } from '../services/api';
import { API_BASE_URL } from '../services/api';

const STORAGE_KEY_PREFIX = 'ca_custom_reports_v1';

const buildStorageKey = (sessionId?: string | null) => {
  const normalized = (sessionId ?? '').trim();
  return normalized.length > 0 ? `${STORAGE_KEY_PREFIX}:${normalized}` : `${STORAGE_KEY_PREFIX}:global`;
};

const normalizeFlag = (value?: string | null) => {
  if (!value) return false;
  return value.toLowerCase() === 'true' || value === '1' || value.toLowerCase() === 'yes';
};

export const ENABLE_CUSTOM_REPORTS =
  normalizeFlag(process.env.REACT_APP_ENABLE_CUSTOM_REPORTS) || true;

const safeParse = (storageKey: string): MicroReport[] => {
  if (typeof window === 'undefined') return [];
  try {
    let stored = window.localStorage.getItem(storageKey);
    if (!stored && storageKey.endsWith(':global')) {
      stored = window.localStorage.getItem(STORAGE_KEY_PREFIX);
    }
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((entry) => entry && typeof entry === 'object')
      .map((entry) => ({
        id: String(entry.id ?? `custom-${Date.now()}`),
        title: String(entry.title ?? 'Custom Analysis'),
        date: entry.date ?? new Date().toISOString(),
        content: String(entry.content ?? ''),
        type: 'custom',
      }));
  } catch (err) {
    console.warn('[customReports] Failed to parse stored reports', err);
    return [];
  }
};

const persist = (storageKey: string, reports: MicroReport[]) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(reports));
    if (storageKey.endsWith(':global')) {
      window.localStorage.removeItem(STORAGE_KEY_PREFIX);
    }
  } catch (err) {
    console.warn('[customReports] Failed to persist reports', err);
  }
};

const buildReport = (overrides: Partial<MicroReport> = {}, count = 0): MicroReport => {
  const now = new Date().toISOString();
  const id =
    overrides.id ??
    (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `custom-${Date.now()}-${Math.random().toString(16).slice(2)}`);
  return {
    id,
    title: overrides.title ?? `Custom Analysis ${count + 1}`,
    date: overrides.date ?? now,
    content: overrides.content ?? '',
    type: 'custom',
  };
};

export const useCustomReportProgram = (options?: { enabled?: boolean; sessionId?: string | null }) => {
  const envEnabled = ENABLE_CUSTOM_REPORTS;
  const enabled = options?.enabled ?? envEnabled;
  const storageKey = useMemo(() => buildStorageKey(options?.sessionId), [options?.sessionId]);

  const [customReports, setCustomReports] = useState<MicroReport[]>(() => {
    if (!enabled) return [];
    return safeParse(storageKey);
  });

  useEffect(() => {
    if (!enabled) return;
    setCustomReports(safeParse(storageKey));
  }, [enabled, storageKey]);

  useEffect(() => {
    if (!enabled) return;
    persist(storageKey, customReports);
  }, [enabled, storageKey, customReports]);

  const createCustomReport = useCallback(
    (seed?: Partial<MicroReport>): MicroReport | null => {
      if (!enabled) return null;
      const next = buildReport(seed, customReports.length);
      setCustomReports((prev) => [...prev, next]);
      try {
        fetch(`${API_BASE_URL}/log-custom-report`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: next.title || 'Custom Analysis' }),
        }).catch(() => {});
      } catch (err) {
        // Ignore logging errors
      }
      return next;
    },
    [enabled, customReports.length]
  );

  const updateCustomReport = useCallback(
    (id: string, updates: Partial<MicroReport>): MicroReport | null => {
      if (!enabled) return null;
      let nextValue: MicroReport | null = null;
      setCustomReports((prev) => {
        let didChange = false;
        const nextReports = prev.map((report) => {
          if (report.id !== id) return report;
          const nextContent = updates.content ?? report.content;
          const nextTitle = updates.title ?? report.title;
          const nextDate = updates.content || updates.title ? new Date().toISOString() : report.date;
          const hasMeaningfulChange =
            nextContent !== report.content || nextTitle !== report.title || nextDate !== report.date;
          if (!hasMeaningfulChange) {
            nextValue = report;
            return report;
          }
          didChange = true;
          const merged: MicroReport = {
            ...report,
            ...updates,
            content: nextContent,
            title: nextTitle,
            type: 'custom',
            date: nextDate,
          };
          nextValue = merged;
          return merged;
        });
        return didChange ? nextReports : prev;
      });
      return nextValue;
    },
    [enabled]
  );

  const deleteCustomReport = useCallback(
    (id: string) => {
      if (!enabled) return;
      setCustomReports((prev) => prev.filter((report) => report.id !== id));
    },
    [enabled]
  );

  const resetCustomReports = useCallback(() => {
    if (!enabled) return;
    setCustomReports([]);
  }, [enabled]);

  const program = useMemo(
    () => ({
      enabled,
      customReports,
      createCustomReport,
      updateCustomReport,
      deleteCustomReport,
      resetCustomReports,
    }),
    [enabled, customReports, createCustomReport, updateCustomReport, deleteCustomReport, resetCustomReports]
  );

  return program;
};

export type CustomReportProgram = ReturnType<typeof useCustomReportProgram>;

